import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import ScrollReveal from "@/components/common/ScrollReveal";
import SectionHeading from "@/components/common/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FORMS } from "@/constants/testIds";
import { postForm } from "@/lib/api";
import { productCategories } from "@/data/products";

const workflow = [
    { n: "01", t: "Requirement collection", d: "Category, specs, target price, MOQ, packaging, market compliance." },
    { n: "02", t: "Supplier identification", d: "3–5 factory shortlist from our audited cluster network." },
    { n: "03", t: "Supplier verification", d: "Company docs, capacity, past shipments, GST/IEC/export history." },
    { n: "04", t: "Sample development", d: "Prototype-to-approval cycle, revisions, buyer sign-off." },
    { n: "05", t: "Factory audit", d: "On-site audit — machinery, workforce, QC systems, ethical compliance." },
    { n: "06", t: "Quality inspection", d: "In-process + pre-shipment inspection with photo evidence." },
    { n: "07", t: "OEM / private label", d: "Custom branding, hangtags, care labels, retail packaging." },
    { n: "08", t: "Packaging", d: "Poly-bag prints, cartonisation, master-carton, palletisation." },
    { n: "09", t: "Shipping", d: "Freight, docs, customs, insurance — CIF / FOB / DAP." },
];

const initialForm = {
    name: "", email: "", phone: "", company: "", country: "",
    product_category: "towels", product_description: "", target_price: "", quantity: "",
    target_markets: "", packaging_requirements: "", certifications: "", private_label: false,
    timeline: "", additional_notes: "",
};

export default function CustomSourcing() {
    const { t } = useTranslation();
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postForm("/sourcing", form);
            toast.success(t("toast.sourcingReceived"));
            setForm(initialForm);
        } catch { toast.error(t("toast.genericError")); }
        finally { setLoading(false); }
    };
    return (
        <>
            <SEO
                title="Custom Product Sourcing from India | Exportfy.world"
                description="End-to-end custom sourcing from India — requirement collection, supplier identification & verification, sample development, factory audit, QC, OEM/private label, packaging, and shipping."
                path="/custom-sourcing"
            />
            <PageHero
                eyebrow={t("customSourcing.hero.eyebrow")}
                title={t("customSourcing.hero.title")}
                subtitle={t("customSourcing.hero.subtitle")}
            />
            <section className="section-y">
                <div className="container-x">
                    <ScrollReveal><SectionHeading eyebrow={t("customSourcing.workflow.eyebrow")} title={t("customSourcing.workflow.title")} /></ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {workflow.map((w, i) => (
                            <ScrollReveal key={w.n} delay={i * 40}>
                                <div className="bg-white border border-slate-200 rounded-2xl p-6 h-full hover:border-[#1D4ED8] transition-colors">
                                    <div className="text-emerald-600 font-extrabold text-sm tracking-widest">{w.n}</div>
                                    <div className="mt-3 font-bold text-lg text-[#0A2540]">{t(`customSourcing.workflow.steps.${i + 1}.title`)}</div>
                                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t(`customSourcing.workflow.steps.${i + 1}.description`)}</p>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-y bg-[#F8FAFC]">
                <div className="container-x max-w-4xl">
                    <ScrollReveal><SectionHeading eyebrow={t("customSourcing.form.eyebrow")} title={t("customSourcing.form.title")} subtitle={t("customSourcing.form.subtitle")} /></ScrollReveal>
                    <ScrollReveal delay={80}>
                        <form onSubmit={submit} className="mt-12 bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-900/5 p-8">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input required placeholder={t("customSourcing.form.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={FORMS.sourcing.name} />
                                <Input required type="email" placeholder={t("customSourcing.form.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={FORMS.sourcing.email} />
                                <Input placeholder={t("customSourcing.form.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={FORMS.sourcing.phone} />
                                <Input placeholder={t("customSourcing.form.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid={FORMS.sourcing.company} />
                                <Input placeholder={t("customSourcing.form.country")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid={FORMS.sourcing.country} />
                                <Select value={form.product_category} onValueChange={(v) => setForm({ ...form, product_category: v })}>
                                    <SelectTrigger data-testid={FORMS.sourcing.category}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {productCategories.map((c) => <SelectItem key={c.slug} value={c.slug}>{t(c.nameKey)}</SelectItem>)}
                                        <SelectItem value="other">{t("customSourcing.form.other")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Textarea rows={4} required placeholder={t("customSourcing.form.productDescription")} className="mt-4" value={form.product_description} onChange={(e) => setForm({ ...form, product_description: e.target.value })} data-testid={FORMS.sourcing.description} />
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input placeholder={t("customSourcing.form.quantity")} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid={FORMS.sourcing.quantity} />
                                <Input placeholder={t("customSourcing.form.targetPrice")} value={form.target_price} onChange={(e) => setForm({ ...form, target_price: e.target.value })} data-testid={FORMS.sourcing.targetPrice} />
                                <Input placeholder={t("customSourcing.form.targetMarkets")} value={form.target_markets} onChange={(e) => setForm({ ...form, target_markets: e.target.value })} data-testid={FORMS.sourcing.markets} />
                                <Input placeholder={t("customSourcing.form.packaging")} value={form.packaging_requirements} onChange={(e) => setForm({ ...form, packaging_requirements: e.target.value })} data-testid={FORMS.sourcing.packaging} />
                                <Input placeholder={t("customSourcing.form.certifications")} value={form.certifications} onChange={(e) => setForm({ ...form, certifications: e.target.value })} data-testid={FORMS.sourcing.certifications} />
                                <Input placeholder={t("customSourcing.form.timeline")} value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} data-testid={FORMS.sourcing.timeline} />
                            </div>
                            <label className="mt-4 flex items-center gap-3 text-sm text-slate-700">
                                <Checkbox checked={form.private_label} onCheckedChange={(v) => setForm({ ...form, private_label: !!v })} data-testid={FORMS.sourcing.privateLabel} />
                                {t("customSourcing.form.privateLabel")}
                            </label>
                            <Textarea rows={3} placeholder={t("customSourcing.form.notes")} className="mt-4" value={form.additional_notes} onChange={(e) => setForm({ ...form, additional_notes: e.target.value })} data-testid={FORMS.sourcing.notes} />
                            <Button type="submit" disabled={loading} data-testid={FORMS.sourcing.submit} className="mt-6 rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold px-8 py-6">
                                {loading ? t("customSourcing.form.sending") : t("customSourcing.form.submit")}
                            </Button>
                        </form>
                    </ScrollReveal>
                </div>
            </section>
        </>
    );
}
